/* eslint-disable eqeqeq */
import { ChildSeatType, LuggageType, PassengerType } from "@daytrip/legacy-enums";
import { transformPhoneNumberToNormalizedForm } from "@daytrip/utils";
import { Exclude, Expose, Transform, Type } from "class-transformer";
import {
    IsArray,
    IsDate,
    IsDefined,
    IsEmail,
    IsNumber,
    IsOptional,
    IsString,
    IsUUID,
    MaxLength,
    ValidateIf,
} from "class-validator";

import { GAuthorized, GField, GInputType, GInt, GObjectType } from "../graphql";
import { CDColumn } from "../orm";
import { TransformToDateWithoutTime } from "../transformers/TransformToDateWithoutTime";
import { stringIsEmail } from "../utils/strings";

import { UserSharedPosition } from "./UserSharedPosition";

@GObjectType("Passenger")
@GInputType("PassengerInput")
@Exclude()
export class Passenger {
    @GField(() => String)
    @IsString()
    @IsDefined()
    @Expose()
    public type: PassengerType;

    @GField({ nullable: true })
    @IsUUID("4")
    @IsDefined()
    @ValidateIf((passenger) => passenger.type === PassengerType.Lead)
    @Expose()
    // eslint-disable-next-line eqeqeq
    @Transform((value) => (value == undefined ? undefined : String(value)))
    public userId?: string;

    @GAuthorized()
    @GField({ nullable: true })
    @IsDate()
    @ValidateIf((passenger) => passenger.type === PassengerType.Lead || passenger.type === PassengerType.Child)
    @Expose()
    @TransformToDateWithoutTime
    public birthdayAt?: Date;

    @GField({ nullable: true })
    @MaxLength(64)
    @IsString()
    // @IsDefined()
    @ValidateIf((passenger) => passenger.type === PassengerType.Lead)
    @Expose()
    public firstName?: string;

    @GField({ nullable: true })
    @MaxLength(64)
    @IsString()
    // @IsDefined()
    @ValidateIf((passenger) => passenger.type === PassengerType.Lead)
    @Expose()
    public lastName?: string;

    @GAuthorized()
    @GField({ nullable: true })
    @Transform((value: string) => (!stringIsEmail(value) ? undefined : value.trim()))
    @ValidateIf((passenger) => passenger.type !== PassengerType.Lead)
    @MaxLength(256)
    @IsEmail()
    @Transform((value: string) => (value != undefined || value != null ? value.trim() : undefined))
    @IsString()
    @IsDefined()
    @ValidateIf((passenger) => passenger.type === PassengerType.Lead)
    @Expose()
    public email?: string;

    @GField({ nullable: true })
    @IsUUID("4")
    // @IsDefined()
    // @ValidateIf((passenger) => passenger.type == PassengerType.Lead)
    @Expose()
    public countryId?: string;

    @GField(() => String, { nullable: true })
    @IsUUID("4")
    @Expose()
    public assignationId: string | null = null;

    @GField({ nullable: true })
    @MaxLength(32)
    @IsString()
    @Transform((value) => transformPhoneNumberToNormalizedForm(value))
    @Expose()
    public phoneNumber?: string;

    @GField(() => GInt, { nullable: true })
    @IsNumber()
    @ValidateIf((passenger) => passenger.type === PassengerType.Child)
    @Expose()
    public childSeatType?: ChildSeatType;

    @GField(() => [Number], { nullable: true })
    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => Number)
    public luggage: LuggageType[] = [LuggageType.CarryOn, LuggageType.Suitcase];

    @GField(() => UserSharedPosition, { nullable: true })
    @CDColumn()
    @Expose()
    @IsOptional()
    public sharedPosition?: UserSharedPosition;

    public isLead(): this is LeadPassenger {
        return this.type === PassengerType.Lead;
    }

    public isAdult(): this is AdultPassenger {
        return this.type === PassengerType.Adult;
    }

    public isChild(): this is ChildPassenger {
        return this.type === PassengerType.Child;
    }

    public get age(): number {
        const today = new Date();
        return today.getUTCFullYear() - (this.birthdayAt as Date).getUTCFullYear();
    }
}

export interface LeadPassenger {
    birthdayAt: Date;
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    countryId: string;
}

export interface AdultPassenger {
    email: string;
    phoneNumber: string;
    countryId: string;
}

export interface ChildPassenger {
    childSeatType: ChildSeatType;
}
